/* eslint import/prefer-default-export: "off" */
import { flattenDeep, compact, isEmpty } from 'lodash'
import { parseCamURI } from './url.utils'
import { ResourceTypeEnum } from '@src/endpoints/services/commonHdr.service'
import { CloudTypeEnum } from '@src/endpoints/services/base.service'
import { shortenPath, uuid4Regex } from './scope.utils'

const attachedPreffix = 'attached'
export const idSuffix = 'id'.toUpperCase()

export type Policies = string | string[]
export interface PolicyFields {
  policyName?: string
  scope?: string
  id?: string
  group?: string
  role?: string
  azureFnApp?: string
  cloudType?: CloudTypeEnum
}

export const parseLongPolicy = (policy: string): PolicyFields => {
  if (!isEmpty(policy) && policy.split(':').length === 8) {
    const policyParts = policy.match(/([A-Z_]*):(.+):(.+)/)
    const camUri = parseCamURI(policyParts[2])
    const policyName = policyParts[3]
    const scope = camUri.accountId
    const id = camUri.id
    const result = {
      policyName,
      scope,
      id,
      cloudType: camUri.cloudType
    }
    if (policyParts[1] === ResourceTypeEnum.GROUP) {
      return {
        ...result,
        group: id
      }
    }
    if (policyParts[1] === ResourceTypeEnum.AZURE_FUNCTION_APP) {
      return {
        ...result,
        azureFnApp: id
      }
    }
    if (policyParts[1] === ResourceTypeEnum.ROLE) {
      return {
        ...result,
        role: id
      }
    }
    return result
  }
  if (!isEmpty(policy) && policy.split(':').length === 6) {
    const camUri = parseCamURI(policy)
    const policyName = camUri.id
    const scope = camUri.accountId
    return {
      policyName,
      scope,
      id: policyName,
      cloudType: camUri.cloudType
    }
  }
  return null
}

export const parseAttachedPolicy = (policy: string): string => {
  if (!isEmpty(policy) && policy.split(':').length === 2) {
    const parts = policy.split(':')
    if (parts[0].toLowerCase() === attachedPreffix) {
      return `POLICY:${parts[1]}`
    }
  }
  return null
}

export const parsePolicy = (
  policy: string,
  cloudType = CloudTypeEnum.CLOUD_TYPE_UNKNOWN
): Policies => {
  // Empty
  if (policy === undefined || policy.length < 1) return undefined
  if (cloudType === CloudTypeEnum.CLOUD_TYPE_UNKNOWN) return policy
  let entities: string[]

  // Comma policy format
  entities = policy.split(',')
  if (entities.length > 1) {
    // Policies from IDP accounts
    const roleScopeParts = entities[0].split(':')
    const isSaml = entities[1].toLocaleLowerCase().indexOf('saml') >= 0
    const idpParts = entities.length > 2 ? entities[2].split('=') : null
    if (
      roleScopeParts.length === 2 &&
      roleScopeParts[0].toLowerCase() === attachedPreffix
    ) {
      return [
        `${entities.length > 2 ? entities[2] : 'POLICY'}:${
          roleScopeParts[1]
        } ${idSuffix}:${roleScopeParts[1]}`,
        `POLICY:${entities[1]}${
          idpParts ? ` IDP:${idpParts[1]}` : ``
        } ${idSuffix}:${entities[1]}`
      ]
    }
    if (roleScopeParts.length === 8) {
      const policyFields = parseLongPolicy(entities[0])
      const isAzureFnApp = !isEmpty(policyFields.azureFnApp)
      if (isAzureFnApp) {
        const azPath = shortenPath(policyFields.azureFnApp)
        const roleName = entities[1].split('/')[0]
        return [
          `ROLE:${roleName} SCOPE:global ${idSuffix}:${policyFields.policyName}`,
          `ROLE:${roleName} SCOPE:${azPath} ${idSuffix}:${policyFields.policyName}`,
          `ROLE:${roleName} SCOPE:(?)${
            idpParts ? ` IDP:${idpParts[1]}` : ``
          } ${idSuffix}:${policyFields.policyName}`
        ]
      } else {
        return isSaml
          ? [
              `${
                policyFields.role
                  ? `ROLE:${policyFields.role}`
                  : `POLICY:${policyFields.policyName}`
              } SCOPE:${policyFields.scope}${
                idpParts ? ` IDP:${idpParts[1]}` : ``
              } ${idSuffix}:${policyFields.policyName}`,
              `POLICY:${entities[1]}${
                idpParts ? ` IDP:${idpParts[1]}` : ``
              } ${idSuffix}:${entities[1]}`
            ]
          : `${
              policyFields.role
                ? `ROLE:${policyFields.role}`
                : `POLICY:${policyFields.policyName}`
            } SCOPE:${policyFields.scope}${
              idpParts ? ` IDP:${idpParts[1]}` : ``
            } ${idSuffix}:${policyFields.policyName}`
      }
    }

    if (cloudType === CloudTypeEnum.AWS) {
      const policySet = [entities[0], entities[1]].map(p => {
        const parts = p.split(':')
        if (parts.length === 2 && parts[0].toLowerCase() === attachedPreffix) {
          return parseAttachedPolicy(p)
        } else {
          return `${CloudTypeEnum.AWS}:${p} ${idSuffix}:${p}`
        }
      })
      return policySet
    }
  }

  // Attached policy format
  entities = policy.split(':')
  if (entities.length === 2 && entities[0].toLowerCase() === attachedPreffix) {
    if ([CloudTypeEnum.AZURE, CloudTypeEnum.AWS].includes(cloudType)) {
      return `LOOKUP:${entities[1]} ${idSuffix}:${entities[1]}`
    }
    if (cloudType === CloudTypeEnum.GCP) {
      return `${entities[1]} ${idSuffix}:${entities[1]}`
    }
    return parseAttachedPolicy(policy)
  }

  // 8 parts URI policy
  if (entities.length > 2) {
    const policyFields = parseLongPolicy(policy)
    if (
      policyFields.policyName?.match(uuid4Regex) ||
      policyFields.role?.match(uuid4Regex) ||
      policyFields.cloudType === CloudTypeEnum.AZURE
    ) {
      return `LOOKUP:${policyFields.policyName} ${idSuffix}:${policyFields.policyName}`
    }

    if (cloudType === CloudTypeEnum.AZURE && policyFields.role) {
      return `LOOKUP:${policyFields.policyName} ${idSuffix}:${policyFields.policyName}`
    }

    if (cloudType === CloudTypeEnum.AWS && policyFields.group) {
      return `GROUP:${policyFields.group} POLICY:${policyFields.policyName} SCOPE:${policyFields.scope} ${idSuffix}:${policy}`
    }
    if (cloudType === CloudTypeEnum.AWS && policyFields.role) {
      return `ROLE:${policyFields.role} POLICY:${policyFields.policyName} SCOPE:${policyFields.scope} ${idSuffix}:${policy}`
    } else {
      return `POLICY:${policyFields.policyName} SCOPE:${policyFields.scope} ${idSuffix}:${policy}`
    }
  }

  // Azure / policy
  if (policy.split('/').length > 1 && cloudType === CloudTypeEnum.AZURE) {
    const parts = policy.split('/')
    if (parts[1].match(uuid4Regex)) {
      return `LOOKUP:${policy} ${idSuffix}:${policy}`
    }
  }

  return `${policy} ${idSuffix}:${policy}`
}

export const parsePolicies = (data: Policies): Policies[] => {
  if (typeof data === 'string' || data instanceof String) {
    return (data || '').split(',')
  }
  const result = compact(data || [])
    .map(x => (x || '').split(','))
    .flat()
  return result
}

export const getCountPolicies = (policies: Policies[]) =>
  flattenDeep(policies || [])?.length
